import React, { useCallback, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  AlertTriangle, CheckCircle2, Layers, RefreshCw, Search, Shield, Smartphone, Tag,
} from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { isPrimaryAdmin } from '../utils/roleRouting';

// Codes d'anomalie renvoyés par le backend (NE PAS traduire) + mapping vers clés i18n pour l'affichage.
const ISSUE_CODES = ['false_phone_imei', 'needs_imei', 'categorie_mismatch', 'categorie_missing', 'ambiguous'];

const ISSUE_LABEL_KEYS = {
  false_phone_imei: 'stockCatAudit.issueFalsePhoneImei',
  needs_imei: 'stockCatAudit.issueNeedsImei',
  categorie_mismatch: 'stockCatAudit.issueCategorieMismatch',
  categorie_missing: 'stockCatAudit.issueCategorieMissing',
  ambiguous: 'stockCatAudit.issueAmbiguous',
};

const issueBadge = (code) => {
  const map = {
    false_phone_imei: 'bg-red-500/20 text-red-300 border-red-500/30',
    needs_imei: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    categorie_mismatch: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
    categorie_missing: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
    ambiguous: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
  };
  return map[code] || 'bg-white/10 text-gray-400';
};

const StockCategoryAuditPage = () => {
  const { api, user } = useAuth();
  const { t } = useLanguage();
  const [mag, setMag] = useState('');
  const [issuesOnly, setIssuesOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [issueFilter, setIssueFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());

  const issueLabel = useCallback(
    (code) => (ISSUE_LABEL_KEYS[code] ? t(ISSUE_LABEL_KEYS[code]) : code),
    [t],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setSelected(new Set());
    try {
      const res = await api.get('/stock/category-audit', {
        params: {
          magasin_id: mag || undefined,
          issues_only: issuesOnly,
        },
      });
      setReport(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || t('stockCatAudit.errorAnalyze'));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [api, mag, issuesOnly, t]);

  const rows = useMemo(() => {
    if (!report?.rows) return [];
    let list = report.rows;
    if (issueFilter !== 'all') {
      list = list.filter((r) => (r.issues || []).includes(issueFilter));
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (r) =>
          (r.reference || '').toLowerCase().includes(q)
          || (r.designation || '').toLowerCase().includes(q)
          || (r.categorie || '').toLowerCase().includes(q)
          || (r.suggested_categorie || '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [report, search, issueFilter]);

  const toggleAll = () => {
    if (selected.size === rows.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(rows.map((r) => r.id).filter(Boolean)));
    }
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applySuggested = async (ids) => {
    const targets = report?.rows?.filter((r) => ids.has(r.id) && r.suggested_categorie) || [];
    if (!targets.length) return;
    if (!window.confirm(t('stockCatAudit.confirmApply', { count: targets.length }))) return;
    setApplying(true);
    setError('');
    try {
      await api.post('/stock/category-audit/apply', {
        updates: targets.map((r) => ({
          id: r.id,
          categorie: r.suggested_categorie,
          is_phone: r.is_phone,
          is_piece: r.is_piece,
        })),
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || t('stockCatAudit.errorApply'));
    } finally {
      setApplying(false);
    }
  };

  if (!isPrimaryAdmin(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  const stats = report?.stats || {};

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6 fade-in" data-testid="stock-category-audit-page">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Shield className="text-cyan-400" size={26} />
              {t('stockCatAudit.title')}
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              {t('stockCatAudit.subtitle')}
            </p>
          </div>
          <Link to="/admin/integration-apis" className="text-xs text-gray-500 hover:text-gray-300">
            ← {t('stockCatAudit.backToIntegrations')}
          </Link>
        </header>

        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-[10px] text-gray-500 uppercase tracking-wide block mb-1">{t('stockCatAudit.magasin')}</label>
            <select
              value={mag}
              onChange={(e) => setMag(e.target.value)}
              className="input-dark text-sm py-2"
            >
              <option value="">{t('stockCatAudit.allStores')}</option>
              <option value="M1">M1</option>
              <option value="M2">M2</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-400 pb-2 cursor-pointer">
            <input
              type="checkbox"
              checked={issuesOnly}
              onChange={(e) => setIssuesOnly(e.target.checked)}
              className="rounded border-gray-600"
            />
            {t('stockCatAudit.issuesOnly')}
          </label>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-cyan-600 text-white text-sm font-semibold hover:bg-cyan-500 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {t('stockCatAudit.runAnalysis')}
          </button>
        </div>

        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {report && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {[
                [t('stockCatAudit.statTotal'), stats.total, 'text-white'],
                [t('stockCatAudit.statWithIssues'), stats.with_issues, 'text-amber-400'],
                [t('stockCatAudit.statFalseImei'), stats.false_phone_imei, 'text-red-400'],
                [t('stockCatAudit.statNeedsImei'), stats.needs_imei, 'text-orange-300'],
                [t('stockCatAudit.statCategorieMismatch'), stats.categorie_mismatch, 'text-cyan-400'],
                [t('stockCatAudit.statCategorieMissing'), stats.categorie_missing, 'text-gray-400'],
                [t('stockCatAudit.statAmbiguous'), stats.ambiguous, 'text-gray-500'],
              ].map(([label, val, cls]) => (
                <div key={label} className="glass p-3 rounded-xl border border-white/5">
                  <div className={`text-xl font-bold ${cls}`}>{val ?? 0}</div>
                  <div className="text-[10px] text-gray-500 uppercase">{label}</div>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="glass p-4 rounded-xl border border-white/5">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
                  <Tag className="w-4 h-4 text-cyan-400" /> {t('stockCatAudit.currentCategories')}
                </h3>
                <ul className="text-xs space-y-1 max-h-48 overflow-y-auto">
                  {Object.entries(report.by_current_categorie || {}).map(([k, v]) => (
                    <li key={k} className="flex justify-between text-gray-400">
                      <span>{k}</span>
                      <span className="text-gray-300 font-mono">{v}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="glass p-4 rounded-xl border border-white/5">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
                  <Layers className="w-4 h-4 text-emerald-400" /> {t('stockCatAudit.suggestedCategories')}
                </h3>
                <ul className="text-xs space-y-1 max-h-48 overflow-y-auto">
                  {Object.entries(report.by_suggested_categorie || {}).map(([k, v]) => (
                    <li key={k} className="flex justify-between text-gray-400">
                      <span>{k}</span>
                      <span className="text-emerald-400 font-mono">{v}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('stockCatAudit.searchPlaceholder')}
                  className="input-dark w-full pl-9 text-sm py-2"
                />
              </div>
              <select
                value={issueFilter}
                onChange={(e) => setIssueFilter(e.target.value)}
                className="input-dark text-sm py-2"
              >
                <option value="all">{t('stockCatAudit.allFilters')}</option>
                {ISSUE_CODES.map((code) => (
                  <option key={code} value={code}>{issueLabel(code)}</option>
                ))}
              </select>
              <button
                type="button"
                disabled={!selected.size || applying}
                onClick={() => applySuggested(selected)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600/80 text-white text-sm disabled:opacity-40"
              >
                <CheckCircle2 className="w-4 h-4" />
                {t('stockCatAudit.applySuggested', { count: selected.size })}
              </button>
            </div>

            <div className="glass rounded-xl border border-white/5 overflow-hidden">
              <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[#0a0a14] border-b border-white/10">
                    <tr className="text-left text-gray-500">
                      <th className="p-2 w-8">
                        <input
                          type="checkbox"
                          checked={rows.length > 0 && selected.size === rows.length}
                          onChange={toggleAll}
                        />
                      </th>
                      <th className="p-2">{t('stockCatAudit.colRef')}</th>
                      <th className="p-2">{t('stockCatAudit.colDesignation')}</th>
                      <th className="p-2">{t('stockCatAudit.colMag')}</th>
                      <th className="p-2">{t('stockCatAudit.colCurrentCat')}</th>
                      <th className="p-2">{t('stockCatAudit.colSuggestedCat')}</th>
                      <th className="p-2">{t('stockCatAudit.colType')}</th>
                      <th className="p-2">{t('stockCatAudit.colIssues')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id || r.reference} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={selected.has(r.id)}
                            onChange={() => toggleRow(r.id)}
                            disabled={!r.id}
                          />
                        </td>
                        <td className="p-2 font-mono text-amber-400/90">{r.reference}</td>
                        <td className="p-2 text-gray-300 max-w-[240px] truncate" title={r.designation}>{r.designation}</td>
                        <td className="p-2 text-gray-500">{r.magasin_id || '—'}</td>
                        <td className="p-2 text-gray-400">{r.categorie || '—'}</td>
                        <td className="p-2 text-emerald-400">{r.suggested_categorie || '—'}</td>
                        <td className="p-2">
                          {r.needs_imei ? (
                            <span className="inline-flex items-center gap-1 text-amber-400">
                              <Smartphone className="w-3 h-3" /> {t('stockCatAudit.typePhone')}
                            </span>
                          ) : r.is_piece ? (
                            <span className="text-cyan-400">{t('stockCatAudit.typePiece')}</span>
                          ) : (
                            <span className="text-gray-600">—</span>
                          )}
                        </td>
                        <td className="p-2">
                          <div className="flex flex-wrap gap-1">
                            {(r.issues || []).map((code) => (
                              <span
                                key={code}
                                className={`px-1.5 py-0.5 rounded border text-[10px] ${issueBadge(code)}`}
                              >
                                {issueLabel(code)}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!rows.length && (
                <p className="p-8 text-center text-gray-500 text-sm">
                  {loading ? t('stockCatAudit.analyzing') : t('stockCatAudit.emptyRows')}
                </p>
              )}
            </div>

            <p className="text-[11px] text-gray-600 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              {t('stockCatAudit.hint')}
            </p>
          </>
        )}
      </div>
    </Layout>
  );
};

export default StockCategoryAuditPage;
